import type { DocLink } from 'src/types/helios/Question';

/** Schemes accepted for a documentation link. A bare `new URL()` accepts ANY
 *  scheme (`referhttps:`, `foo:bar`, even `javascript:`), so a typo like
 *  `referhttps://…` slips through and renders a broken/unsafe href. The value
 *  is rendered into an anchor `href`, so restrict to real web links. */
export const ALLOWED_URL_PROTOCOLS: readonly string[] = ['http:', 'https:'];

/**
 * True when `raw` parses as an absolute http(s) URL. Stricter than the manual
 * workflow's bare `new URL()` on purpose: the protocol allowlist rejects
 * typo'd schemes and unsafe `javascript:` hrefs before they ever reach an
 * anchor `href`.
 */
export function isValidDocLinkUrl(raw: string): boolean {
  try {
    const url = new URL(raw);
    return ALLOWED_URL_PROTOCOLS.includes(url.protocol);
  } catch {
    return false;
  }
}

/**
 * True when `url` already exists in `value` (case-insensitive compare on the
 * link). Keeping links unique both prevents a confusing duplicate row and
 * keeps each row's React key stable so removal never mis-associates rows.
 * `url` is expected pre-trimmed by the caller.
 */
export function isDuplicateDocLinkUrl(value: readonly DocLink[], url: string): boolean {
  const lower = url.toLowerCase();
  return value.some((item) => item.link?.toLowerCase() === lower);
}
